/**
 * Независимый пересчёт расклада прямо в браузере.
 *
 * Это НЕ обёртка над ответом сервера. Сервер присылает поле `valid`, но
 * доверять ему — то же самое, что доверять «мы ничего не подкручиваем»:
 * проверка, которую делает проверяемый, ничего не проверяет.
 *
 * Поэтому здесь лежит вторая, независимая реализация того же алгоритма.
 * Она написана по договору из pkg/shuffle/README.md и ничего не берёт
 * из ответа, кроме исходных данных: секрета, жеста, номера попытки
 * и канонического порядка колоды.
 *
 * Две реализации на разных языках — ещё и способ поймать ошибку в первой.
 * Расхождение здесь означает, что кто-то из двоих неправ, и узнать об этом
 * лучше в тесте, чем от пользователя.
 */

import type { Proof } from './api'

export interface Recomputed {
  /** SHA-256 от раскрытого секрета совпал с опубликованным до тасовки. */
  hashOk: boolean
  /** Все карты лежат там, где их кладёт пересчитанная перестановка. */
  cardsOk: boolean
  /** Что именно не сошлось. Пусто, когда всё сошлось. */
  mismatches: string[]
}

export async function recompute(proof: Proof): Promise<Recomputed> {
  const serverSeed = fromHex(proof.serverSeed)
  const clientSeed = fromHex(proof.clientSeed)

  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', serverSeed))
  const hashOk = toHex(digest) === proof.serverSeedHash.toLowerCase()

  const stream = new Stream(
    await crypto.subtle.importKey('raw', serverSeed, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']),
    concat(clientSeed, be32(proof.nonce)),
  )

  // Fisher–Yates от конца к началу — ровно как в pkg/shuffle.
  const n = proof.deckOrder.length
  const order = Array.from({ length: n }, (_, i) => i)
  for (let i = n - 1; i > 0; i--) {
    const j = await stream.intn(i + 1)
    const a = order[i] ?? i
    order[i] = order[j] ?? j
    order[j] = a
  }

  // Затем по байту на монетку, младший бит.
  const coin: boolean[] = []
  for (let i = 0; i < n; i++) coin.push(((await stream.byte()) & 1) === 1)

  const mismatches: string[] = []
  for (const card of proof.cards) {
    const expected = proof.deckOrder[order[card.fanSlot] ?? -1] ?? '—'
    if (expected !== card.slug) {
      mismatches.push(`место ${card.fanSlot}: в раскладе ${card.slug}, пересчёт даёт ${expected}`)
    }
    if ((coin[card.fanSlot] ?? false) !== card.reversed) {
      mismatches.push(`место ${card.fanSlot}: ориентация не совпала`)
    }
  }

  return { hashOk, cardsOk: mismatches.length === 0, mismatches }
}

/**
 * Поток байт: блок k = HMAC-SHA256(секрет, жест ‖ попытка ‖ k).
 *
 * Асинхронный, потому что WebCrypto другим не бывает. На 78 картах это
 * около полутора десятков вызовов — незаметно даже на слабом телефоне.
 */
class Stream {
  private block: Uint8Array<ArrayBuffer> = new Uint8Array(0)
  private pos = 32
  private counter = 0

  constructor(
    private readonly key: CryptoKey,
    private readonly msg: Uint8Array<ArrayBuffer>,
  ) {}

  async byte(): Promise<number> {
    if (this.pos >= this.block.length) {
      const input = concat(this.msg, be32(this.counter))
      this.block = new Uint8Array(await crypto.subtle.sign('HMAC', this.key, input))
      this.counter++
      this.pos = 0
    }
    return this.block[this.pos++] ?? 0
  }

  /** Слово в четыре байта со старшего конца. */
  async word(): Promise<number> {
    let w = 0
    // Умножение, а не сдвиг: побитовые операции в JS работают со знаковым
    // 32-битным целым, и всё, что больше 2³¹, стало бы отрицательным.
    for (let i = 0; i < 4; i++) w = w * 256 + (await this.byte())
    return w
  }

  /**
   * Равномерное число из [0, k) с отбрасыванием хвоста.
   *
   * Наивное `word % k` смещало бы распределение — и расхождение с сервером
   * вылезло бы примерно на каждой четырёхмиллиардной карте, то есть никогда
   * при тестах и однажды у пользователя.
   */
  async intn(k: number): Promise<number> {
    if (k <= 1) return 0
    const limit = Math.floor(4294967296 / k) * k
    for (;;) {
      const w = await this.word()
      if (w < limit) return w % k
    }
  }
}

function fromHex(s: string): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(s.length / 2)
  for (let i = 0; i < out.length; i++) out[i] = Number.parseInt(s.substring(i * 2, i * 2 + 2), 16)
  return out
}

function toHex(b: Uint8Array<ArrayBuffer>): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
}

function be32(n: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(4)
  new DataView(out.buffer).setUint32(0, n >>> 0, false)
  return out
}

function concat(a: Uint8Array<ArrayBuffer>, b: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(a.length + b.length)
  out.set(a)
  out.set(b, a.length)
  return out
}
